'use client';

import { Icon, Icons } from '@/components/icon';
import fuzzysort from 'fuzzysort';
import * as React from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Command as CommandPrimitive } from 'cmdk';

type Option = {
  value: string;
  label: string;
};

type ComboboxContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  value: string | string[];
  toggleValue: (value: string) => void;
  multiple: boolean;
  options: Option[];
  filteredOptions: Option[];
};

const ComboboxContext = React.createContext<ComboboxContextValue | null>(null);

function useComboboxContext() {
  const context = React.useContext(ComboboxContext);
  if (!context) {
    throw new Error('Combobox components must be used within Combobox');
  }
  return context;
}

type ComboboxPropsBase = {
  options: Option[];
  trigger: React.ReactElement;
  children?:
    | React.ReactNode
    | ((props: {
        options: Option[];
        filteredOptions: Option[];
      }) => React.ReactNode)
    | Array<
        | React.ReactNode
        | ((props: {
            options: Option[];
            filteredOptions: Option[];
          }) => React.ReactNode)
      >;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  contentClassName?: string;
  listClassName?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  filterMode?: 'fuzzy' | 'includes';
  /** Visual variant for the combobox */
  variant?: 'default' | 'filter';
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  /** Footer content rendered below the list, always visible */
  footer?: React.ReactNode;
  /** Hide the search input */
  hideSearch?: boolean;
};

type ComboboxPropsSingle = ComboboxPropsBase & {
  multiple?: false;
  value?: string;
  onValueChange?: (value: string) => void;
};

type ComboboxPropsMultiple = ComboboxPropsBase & {
  multiple: true;
  value?: string[];
  onValueChange?: (value: string[]) => void;
};

type ComboboxProps = ComboboxPropsSingle | ComboboxPropsMultiple;

function Combobox(props: ComboboxProps) {
  const {
    options,
    value: controlledValue,
    onValueChange,
    trigger,
    children,
    defaultOpen = false,
    open: controlledOpen,
    onOpenChange,
    contentClassName,
    listClassName,
    side,
    align = 'start',
    sideOffset,
    filterMode = 'fuzzy',
    variant = 'default',
    searchPlaceholder,
    footer,
    multiple = false,
    hideSearch = false,
  } = props as ComboboxPropsBase & {
    multiple?: boolean;
    value?: string | string[];
    onValueChange?: (value: string | string[]) => void;
    hideSearch?: boolean;
  };

  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const [internalValue, setInternalValue] = React.useState<string | string[]>(multiple ? [] : '');
  const [searchValue, setSearchValue] = React.useState('');

  const open = controlledOpen ?? internalOpen;
  const setOpen = React.useCallback(
    (newOpen: boolean) => {
      if (controlledOpen === undefined) {
        setInternalOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [controlledOpen, onOpenChange],
  );

  const value = controlledValue ?? internalValue;

  const toggleValue = React.useCallback(
    (itemValue: string) => {
      if (multiple) {
        const currentValues = (Array.isArray(value) ? value : []) as string[];
        const isSelected = currentValues.includes(itemValue);
        const newValues = isSelected
          ? currentValues.filter((v) => v !== itemValue)
          : [...currentValues, itemValue];
        if (controlledValue === undefined) {
          setInternalValue(newValues);
        }
        (onValueChange as ((value: string[]) => void) | undefined)?.(newValues);
      } else {
        const newValue = itemValue === value ? '' : itemValue;
        if (controlledValue === undefined) {
          setInternalValue(newValue);
        }
        (onValueChange as ((value: string) => void) | undefined)?.(newValue);
        setOpen(false);
      }
    },
    [multiple, value, controlledValue, onValueChange, setOpen],
  );

  const filteredOptions = React.useMemo(() => {
    if (!searchValue) {
      return options;
    }

    if (filterMode === 'includes') {
      const normalizedSearch = searchValue.toLowerCase();
      return options.filter((option) => option.label.toLowerCase().includes(normalizedSearch));
    }

    // Use fuzzysort for better search experience
    const results = fuzzysort.go(searchValue, options, {
      key: 'label',
      threshold: -10000, // Show all results, but sorted by relevance
    });

    // Map back to original Option format, maintaining relevance order
    return results.map((result) => result.obj);
  }, [options, searchValue, filterMode]);

  const contextValue = React.useMemo<ComboboxContextValue>(
    () => ({
      open,
      setOpen,
      value,
      toggleValue,
      multiple,
      options,
      filteredOptions,
    }),
    [open, setOpen, value, toggleValue, multiple, options, filteredOptions],
  );

  // Process children - handle both function and regular React nodes
  const renderChildren = () => {
    if (!children) {
      return null;
    }

    // Handle array of children
    if (Array.isArray(children)) {
      return children.map((child, index) => {
        if (typeof child === 'function') {
          const result = child({ options, filteredOptions });
          // Try to preserve key from result if it's a valid element
          if (React.isValidElement(result) && result.key != null) {
            return result;
          }
          // Generate a stable key based on the function or index
          const key = `combobox-child-${index}`;
          return <React.Fragment key={key}>{result}</React.Fragment>;
        }
        // If it's already a valid element with a key, use it
        if (React.isValidElement(child) && child.key != null) {
          return child;
        }
        // Generate a stable key
        const key = `combobox-child-${index}`;
        return <React.Fragment key={key}>{child}</React.Fragment>;
      });
    }

    // Handle function child
    if (typeof children === 'function') {
      return children({ options, filteredOptions });
    }

    // Handle regular React node
    return children;
  };

  // Wrap trigger with PopoverTrigger and add necessary props
  const wrappedTrigger = React.useMemo(() => {
    if (React.isValidElement(trigger)) {
      return (
        <PopoverTrigger asChild>
          {React.cloneElement(
            trigger as React.ReactElement<{
              className?: string;
              role?: string;
              'aria-expanded'?: boolean;
            }>,
            {
              role: 'combobox',
              'aria-expanded': open,
            } as Partial<{
              className?: string;
              role?: string;
              'aria-expanded'?: boolean;
            }>,
          )}
        </PopoverTrigger>
      );
    }
    return <PopoverTrigger asChild>{trigger}</PopoverTrigger>;
  }, [trigger, open]);

  return (
    <ComboboxContext.Provider value={contextValue}>
      <Popover open={open} onOpenChange={setOpen}>
        {wrappedTrigger}
        <PopoverContent
          align={align}
          side={side}
          sideOffset={sideOffset}
          className={cn(
            'min-h-[40px] overflow-hidden border border-primary-5 bg-white p-0',
            variant === 'filter'
              ? 'w-[300px] rounded-2xl shadow-md'
              : 'w-[280px] rounded-xl py-1.5 shadow-lg',
            contentClassName,
          )}
        >
          <Command shouldFilter={false} className="bg-transparent">
            {!hideSearch && (
              <>
                <ComboboxInput
                  variant={variant}
                  placeholder={searchPlaceholder}
                  onValueChange={setSearchValue}
                  showBorder={filteredOptions.length > 0}
                />
                {variant === 'default' && <ComboboxDivider />}
              </>
            )}
            {filteredOptions.length > 0 && (
              <CommandList
                className={cn(
                  'max-h-[192px] overflow-y-auto overflow-x-hidden',
                  variant === 'filter' ? 'max-h-[320px] p-2' : 'px-1 pt-1',
                  listClassName,
                )}
              >
                {renderChildren()}
              </CommandList>
            )}
            {footer}
          </Command>
        </PopoverContent>
      </Popover>
    </ComboboxContext.Provider>
  );
}

type ComboboxTriggerProps = {
  asChild?: boolean;
  children: React.ReactNode;
  className?: string;
};

function ComboboxTrigger({ asChild, children, className }: ComboboxTriggerProps) {
  const { open } = useComboboxContext();
  return (
    <PopoverTrigger asChild={asChild}>
      {React.isValidElement(children)
        ? React.cloneElement(
            children as React.ReactElement<{
              className?: string;
              role?: string;
              'aria-expanded'?: boolean;
            }>,
            {
              role: 'combobox',
              'aria-expanded': open,
              className: cn(
                className,
                (children as React.ReactElement<{ className?: string }>).props.className,
              ),
            } as Partial<{
              className?: string;
              role?: string;
              'aria-expanded'?: boolean;
            }>,
          )
        : children}
    </PopoverTrigger>
  );
}

type ComboboxInputProps = {
  placeholder?: string;
  className?: string;
  onValueChange?: (value: string) => void;
  variant?: 'default' | 'filter';
  showBorder?: boolean;
};

function ComboboxInput({
  placeholder = 'Search...',
  className,
  onValueChange,
  variant = 'default',
  showBorder = true,
}: ComboboxInputProps) {
  const [inputValue, setInputValue] = React.useState('');

  React.useEffect(() => {
    onValueChange?.(inputValue);
  }, [inputValue, onValueChange]);

  if (variant === 'filter') {
    return (
      <div
        className={cn('flex h-[39px] items-center px-3', showBorder && 'border-b border-primary-5')}
      >
        <CommandPrimitive.Input
          placeholder={placeholder}
          className={cn(
            'w-full bg-transparent caret-highlight-default font-inter text-s leading-[15px] font-medium text-primary-100 outline-none placeholder:text-primary-60',
            className,
          )}
          value={inputValue}
          onValueChange={setInputValue}
          autoFocus
        />
      </div>
    );
  }

  return (
    <div className="flex h-7 items-center gap-1 px-2 py-1">
      <Icon name={Icons.MagnifyingGlass} className="text-s shrink-0 text-primary-50" />
      <CommandPrimitive.Input
        placeholder={placeholder}
        className={cn(
          'flex-1 bg-transparent caret-highlight-default font-inter text-s font-medium text-primary-100 outline-none placeholder:text-primary-60',
          className,
        )}
        value={inputValue}
        onValueChange={setInputValue}
      />
    </div>
  );
}

function ComboboxDivider() {
  return <div className="h-px w-full rounded-xs bg-primary-10" />;
}

type ComboboxEmptyProps = {
  children: React.ReactNode;
};

function ComboboxEmpty({ children }: ComboboxEmptyProps) {
  return (
    <CommandEmpty className="py-4 text-center font-inter text-s font-medium text-primary-60">
      {children}
    </CommandEmpty>
  );
}

type ComboboxGroupProps = {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'filter';
};

function ComboboxGroup({ children, className, variant = 'default' }: ComboboxGroupProps) {
  return (
    <CommandGroup className={cn(variant === 'filter' && 'flex flex-col gap-1', className)}>
      {children}
    </CommandGroup>
  );
}

type ComboboxItemProps = {
  option: Option;
  children?: (option: Option, isSelected: boolean) => React.ReactNode;
  className?: string;
  variant?: 'default' | 'filter';
};

function ComboboxItem({ option, children, className, variant = 'default' }: ComboboxItemProps) {
  const { value, toggleValue, multiple } = useComboboxContext();
  const isSelected = multiple
    ? (Array.isArray(value) ? value : []).includes(option.value)
    : value === option.value;

  return (
    <CommandItem
      value={option.value}
      onSelect={() => {
        toggleValue(option.value);
      }}
      className={cn(
        'mx-0 flex cursor-pointer items-center justify-between rounded-md bg-transparent text-s font-medium outline-none',
        variant === 'filter'
          ? 'h-6 gap-1 px-1 py-0 data-[selected=true]:bg-primary-3'
          : 'h-auto gap-2 px-1 py-1 data-[selected=true]:bg-primary-3',
        className,
      )}
    >
      {children ? (
        children(option, isSelected)
      ) : (
        <>
          {option.label}
          <Icon
            name={Icons.Check}
            className={cn(
              'size-3 text-highlight-default',
              isSelected ? 'opacity-100' : 'opacity-0',
            )}
          />
        </>
      )}
    </CommandItem>
  );
}

export {
  Combobox,
  ComboboxTrigger,
  ComboboxInput,
  ComboboxDivider,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
  useComboboxContext,
};
export type { Option, ComboboxProps };
